import { useCallback, useEffect, useState } from "react";
import { request } from "../api/client";
import { parseOwnedSubscriptionPage, subscriptionKey, type OwnedSubscription } from "./ownedSubscriptions";

export function useOwnedSubscriptions(enabled: boolean, organizationId?: string) {
  const [items, setItems] = useState<OwnedSubscription[] | null>(null);
  const [error, setError] = useState<unknown>();
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision(n => n + 1), []);
  useEffect(() => {
    const controller = new AbortController();
    setItems(null); setError(undefined);
    if (!enabled) return () => controller.abort();
    async function load() {
      let cursor = "";
      const cursors = new Set<string>();
      const found = new Map<string, OwnedSubscription>();
      do {
        if (cursors.has(cursor) || cursors.size >= 100) throw new Error("Invalid subscription pagination");
        cursors.add(cursor);
        const page = parseOwnedSubscriptionPage(await request<unknown>("/commerce/subscriptions" + (cursor ? "?" + new URLSearchParams({ cursor }) : ""), "GET", undefined,
          AbortSignal.any([controller.signal, AbortSignal.timeout(15_000)])));
        for (const item of page.items) if (!organizationId || item.organizationId === organizationId) found.set(subscriptionKey(item), item);
        cursor = page.nextCursor || "";
      } while (cursor && !controller.signal.aborted);
      if (!controller.signal.aborted) setItems([...found.values()]);
    }
    void load().catch(cause => { if (!controller.signal.aborted) { setItems(null); setError(cause); } });
    return () => controller.abort();
  }, [enabled, organizationId, revision]);
  useEffect(() => {
    if (!enabled) return;
    const revalidate = () => { if (!document.hidden) refresh(); };
    const timer = window.setInterval(revalidate, 60_000);
    window.addEventListener("focus", revalidate);
    window.addEventListener("mzn:billing-changed", refresh);
    window.addEventListener("mzn:access-check", revalidate);
    document.addEventListener("visibilitychange", revalidate);
    return () => { clearInterval(timer); window.removeEventListener("focus", revalidate); window.removeEventListener("mzn:billing-changed", refresh); window.removeEventListener("mzn:access-check", revalidate); document.removeEventListener("visibilitychange", revalidate); };
  }, [enabled, refresh]);
  return { items: items && items.filter(item => !organizationId || item.organizationId === organizationId), error, refresh };
}
